import { useQuery } from 'react-query';
import config from '../config';
import { apiRequest } from '../services/api';
import {queryClient} from "../services/queryClient";
import {getCacheKeyForEnergy} from "./useEnergy";
import {getCacheKeyForHousePrices} from "./useHousePrices";
import {getCacheKeyForPlanning} from "./usePlanning";
import {getCacheKeyForTransport} from "./useTransport";
import {getCacheKeyForSchool} from "./useSchoolsData";

const fetchSearchById = async (searchId) => {
  // Get the token from localStorage
  const token = localStorage.getItem('token');
  if (!token || !searchId) {
    console.log('No token or search ID available, cannot retrieve search');
    return null;
  }

  // Make the API request without caching (passing `null` for cache key)
  const response = await apiRequest(
    `${config.apiUrl}/search_history/get_search/${searchId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
    null // No cache key
  );

  // If response.api_data exists and is a string, try to parse it as JSON
  if (response && response.api_data && typeof response.api_data === 'string') {
    try {
      response.apiData = JSON.parse(response.api_data);
      console.log('Successfully parsed API data from string');
    } catch (parseError) {
      console.error('Error parsing API data:', parseError);
      response.apiData = null;
    }
  } else if (response && response.api_data) {
    // If it is already an object, just assign it.
    response.apiData = response.api_data;
  }

  return response;
};

const useSearchById = (searchId) => {
  return useQuery({
    queryKey: ['searchById', searchId],
    queryFn: () => fetchSearchById(searchId),
    enabled: !!searchId,
    onSuccess: async (data)  => {
      const responseData = data?.api_response_data;
      const responseObject = JSON.parse(responseData);

      // Prefill energy api response data...
      const address = {
        flat_number: data.flat_number,
        address: data.full_address,
        postcode: data.postcode,
      };
      const energyKey = ["energyData", getCacheKeyForEnergy(address), searchId];
      if(responseObject?.energy) {
        await queryClient.prefetchQuery(energyKey, () => new Promise((resolve) =>resolve(responseObject?.energy)))
      }

      // Prefill housePrices api response data...
      const housePricesKey = ["housePrices", getCacheKeyForHousePrices(address?.postcode), searchId];
      if (responseObject?.housePrices) {
        await queryClient.prefetchQuery(housePricesKey, () => new Promise((resolve) => resolve(responseObject?.housePrices)));
      }

      // Prefill planningData api response data...
      const planningDataKey = ["planningData", getCacheKeyForPlanning({lng: data?.longitude, lat: data?.latitude, postcode: data?.postcode}), searchId];
      if (responseObject?.planning) {
        await queryClient.prefetchQuery(planningDataKey, () => new Promise((resolve) => resolve(responseObject?.planning)));
      }

      // Prefill transportData api response data...
      const transportDataKey = ["transportData", getCacheKeyForTransport({lng: data?.longitude, lat: data?.latitude}), searchId];
      if (responseObject?.transport) {
        await queryClient.prefetchQuery(transportDataKey, () => new Promise((resolve) => resolve(responseObject?.transport)));
      }

      // Prefill schoolsData api response data...
      const schoolsDataKey = ["schoolsData", getCacheKeyForSchool({lng: data?.longitude, lat: data?.latitude}), searchId];
      if (responseObject?.schools) {}
    }
  });
};

export default useSearchById;